/**
 * API consultations partagée établissement / professionnel : assignation par l'établissement
 * (type 1), consultation rapide (type 2), acceptation/refus, réassignation, révocation a
 * posteriori. Voir docs/4-CLARIFICATION_RELATION_ETABLISSEMENT_PATIENT/
 * 6-LIAISONS_ACTEUR_PROCESS_CONSULTATION.md.
 */

import { apiFetch } from "./client";

function extractPayload<T>(raw: unknown): T | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "object" && !Array.isArray(raw) && "data" in raw) {
    return (raw as { data?: T }).data;
  }
  return raw as T;
}

export type StatutAssignation = "directe" | "en_attente" | "acceptee" | "refusee";
export type StatutConsultation = "ouverte" | "fermee" | "annulee";

export type Consultation = {
  id: string;
  patientNip: string;
  professionnelNip: string;
  etablissementId: string;
  date: string;
  typeConsultation: string;
  motif: string;
  observations?: string;
  complement?: unknown;
  statutConsultation: StatutConsultation;
  etatSortie: string | null;
  dateCloture: string | null;
  documents?: unknown[];
  pdfUrl?: string | null;
  dateCreation: string;
  dateModification: string;
  createur: string;
  statutAssignation: StatutAssignation;
  dateAssignation: string | null;
  dateRevocationEtablissement: string | null;
  [key: string]: unknown;
};

export type Pagination = { page: number; limit: number; total: number; totalPages: number };

export type AssignConsultationBody = {
  patientNip: string;
  professionnelNip: string;
  typeConsultation: string;
  motif: string;
  date: string;
  observations?: string;
};

/** 12h sans réponse = éligible à la réassignation (mêmes règles que le backend, pré-check UI). */
export function isReassignmentEligible(c: Consultation): boolean {
  if (c.statutAssignation === "refusee") return true;
  if (c.statutAssignation !== "en_attente" || !c.dateAssignation) return false;
  return Date.now() - new Date(c.dateAssignation).getTime() > 12 * 60 * 60 * 1000;
}

// --- Côté établissement : POST /etablissement/etablissements/:id/consultations

export async function assignConsultation(
  etablissementId: string,
  body: AssignConsultationBody
): Promise<{ ok: true; data: Consultation } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(
    `/etablissement/etablissements/${encodeURIComponent(etablissementId)}/consultations`,
    { method: "POST", body: JSON.stringify(body) }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload<Consultation>(out.data) as Consultation };
}

export async function getAssignedConsultationsForEtablissement(
  etablissementId: string,
  origine: "assignee" | "directe",
  page = 1,
  limit = 20
): Promise<{ ok: true; data: Consultation[]; pagination: Pagination } | { ok: false; message: string }> {
  const q = new URLSearchParams({ origine, page: String(page), limit: String(limit) });
  const out = await apiFetch<unknown>(
    `/etablissement/etablissements/${encodeURIComponent(etablissementId)}/consultations?${q.toString()}`
  );
  if (!out.ok) return { ok: false, message: out.message };
  const root = out.data as { data?: unknown; pagination?: Pagination } | Consultation[];
  const list = Array.isArray(root) ? root : Array.isArray(root?.data) ? (root.data as Consultation[]) : [];
  const pagination = Array.isArray(root)
    ? { page, limit, total: list.length, totalPages: 1 }
    : (root?.pagination ?? { page, limit, total: list.length, totalPages: 1 });
  return { ok: true, data: list, pagination };
}

export async function reassignConsultation(
  etablissementId: string,
  consultationId: string,
  professionnelNip: string
): Promise<{ ok: true; data: Consultation } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(
    `/etablissement/etablissements/${encodeURIComponent(etablissementId)}/consultations/${encodeURIComponent(consultationId)}/reassign`,
    { method: "POST", body: JSON.stringify({ professionnelNip }) }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload<Consultation>(out.data) as Consultation };
}

export async function revokeConsultationLink(
  etablissementId: string,
  consultationId: string
): Promise<{ ok: true; data: Consultation } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(
    `/etablissement/etablissements/${encodeURIComponent(etablissementId)}/consultations/${encodeURIComponent(consultationId)}/revoke`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload<Consultation>(out.data) as Consultation };
}

// --- Côté professionnel : POST /patient/consultations/:id/{accept,refuse}, GET .../assigned

export async function acceptConsultationAssignment(
  consultationId: string
): Promise<{ ok: true; data: Consultation } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(
    `/patient/consultations/${encodeURIComponent(consultationId)}/accept`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload<Consultation>(out.data) as Consultation };
}

export async function refuseConsultationAssignment(
  consultationId: string,
  motif?: string
): Promise<{ ok: true; data: Consultation } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(
    `/patient/consultations/${encodeURIComponent(consultationId)}/refuse`,
    { method: "POST", body: JSON.stringify(motif ? { motif } : {}) }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload<Consultation>(out.data) as Consultation };
}

export async function getAssignedConsultationsForProfessionnel(
  nip: string,
  page = 1,
  limit = 20
): Promise<{ ok: true; data: Consultation[]; pagination: Pagination } | { ok: false; message: string }> {
  const q = new URLSearchParams({ page: String(page), limit: String(limit) });
  const out = await apiFetch<unknown>(
    `/patient/consultations/professionnel/${encodeURIComponent(nip)}/assigned?${q.toString()}`
  );
  if (!out.ok) return { ok: false, message: out.message };
  const root = out.data as { data?: unknown; pagination?: Pagination } | Consultation[];
  const list = Array.isArray(root) ? root : Array.isArray(root?.data) ? (root.data as Consultation[]) : [];
  const pagination = Array.isArray(root)
    ? { page, limit, total: list.length, totalPages: 1 }
    : (root?.pagination ?? { page, limit, total: list.length, totalPages: 1 });
  return { ok: true, data: list, pagination };
}
